import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { RefreshCw, Plus, Trash2, Pencil, UserCog, Users } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { managersAPI } from '../lib/api';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAccountingRole, formatPhone, formatPhoneInput } from '@/lib/utils';

const blankForm = {
  full_name: '', email: '', phone: '', active_status: true,
  npn: '', license: '', notes: '',
};

export default function ManagersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(blankForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await managersAPI.list(search ? { search } : {});
      setRows(r.data.managers || []);
    } catch (e) {
      toast.error('Failed to load managers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  if (user && !isKRRole(user) && !isAccountingRole(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const openCreate = () => { setDialog({ open: true, editing: null }); setForm(blankForm); };
  const openEdit = (m) => {
    setDialog({ open: true, editing: m });
    setForm({
      full_name: m.full_name || '',
      email: m.email || '',
      phone: m.phone || '',
      active_status: m.active_status !== false,
      npn: m.npn || '',
      license: m.license || '',
      notes: m.notes || '',
    });
  };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    const payload = {
      ...form,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      npn: form.npn.trim() || null,
      license: form.license.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (dialog.editing) {
        await managersAPI.update(dialog.editing.manager_id, payload);
        toast.success('Manager updated');
      } else {
        await managersAPI.create(payload);
        toast.success('Manager created');
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete manager "${m.full_name}"?`)) return;
    try {
      await managersAPI.remove(m.manager_id);
      toast.success('Manager deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="managers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <UserCog className="h-7 w-7 text-sky-600" /> Managers & Development Leaders
          </h1>
          <p className="text-zinc-500 mt-1">KR-internal employees who supervise agents and earn Manager bonuses. NPN is optional.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="managers-search-input"
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="managers-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button onClick={openCreate} data-testid="managers-new"><Plus className="h-4 w-4 mr-1" />New Manager</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>NPN</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Direct reports</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-10">No managers yet. Click "New Manager" to add one.</TableCell></TableRow>
              )}
              {rows.map((m) => (
                <TableRow key={m.manager_id} data-testid={`manager-row-${m.manager_id}`}>
                  <TableCell className="font-medium">{m.full_name}</TableCell>
                  <TableCell className="text-sm">{m.email || '—'}</TableCell>
                  <TableCell className="text-sm">{formatPhone(m.phone) || '—'}</TableCell>
                  <TableCell className="text-sm">{m.npn || <span className="text-zinc-400 italic">—</span>}</TableCell>
                  <TableCell className="text-sm">{m.license || <span className="text-zinc-400 italic">—</span>}</TableCell>
                  <TableCell className="text-sm">
                    <DirectReportsStack manager={m} />
                  </TableCell>
                  <TableCell>
                    {m.active_status ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge>Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`manager-edit-${m.manager_id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(m)} data-testid={`manager-delete-${m.manager_id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? 'Edit Manager' : 'New Manager'}</DialogTitle>
            <DialogDescription>Managers are KR-internal supervisors. NPN and license are optional — they're only relevant if the person is also a licensed agent.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Full Name *</Label>
              <Input data-testid="manager-form-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="manager-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="manager-form-phone" value={formatPhoneInput(form.phone)} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>NPN <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
              <Input data-testid="manager-form-npn" value={form.npn} onChange={(e) => setForm({ ...form, npn: e.target.value })} placeholder="If also a licensed agent" />
            </div>
            <div>
              <Label>License # <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
              <Input data-testid="manager-form-license" value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="manager-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch checked={form.active_status} onCheckedChange={(v) => setForm({ ...form, active_status: v })} data-testid="manager-form-active" />
              <Label>Active</Label>
              <span className="text-xs text-slate-500 ml-2">Inactive managers will not receive any Manager payout.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={save} data-testid="manager-form-save">{dialog.editing ? 'Update' : 'Create'} Manager</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Phase 27.30.23 — Compact avatar stack for the "Direct reports" column.
// Shows up to 4 overlapping mini-avatars plus a "+N" pill when there are
// more. Empty state renders a soft placeholder so the column stays
// legible even for managers with no assigned agents yet.
function DirectReportsStack({ manager }) {
  const sample = manager?.direct_reports_sample || [];
  const total = manager?.direct_reports_count ?? sample.length;
  if (total === 0) {
    return <span className="text-zinc-400 italic">—</span>;
  }
  const shown = sample.slice(0, 4);
  const overflow = total - shown.length;
  return (
    <div className="flex items-center gap-2" data-testid={`manager-reports-${manager.manager_id}`}>
      <Users className="h-3.5 w-3.5 text-zinc-400" />
      <div className="flex -space-x-2">
        {shown.map((a) => (
          <div
            key={a.npn}
            className="ring-2 ring-white rounded-full"
            title={`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.npn}
          >
            <AgentAvatar
              agent={a}
              size="w-7 h-7"
              rounded="full"
              textSize="text-[10px]"
              testId={`manager-report-avatar-${manager.manager_id}-${a.npn}`}
            />
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="w-7 h-7 rounded-full ring-2 ring-white bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center"
            title={`${overflow} more`}
          >
            +{overflow}
          </div>
        )}
      </div>
      <span className="text-xs text-zinc-500">{total} {total === 1 ? 'agent' : 'agents'}</span>
    </div>
  );
}
